import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../../context/AuthContext";
import {
  Send, Sparkles, Mail, MessageSquare, Users, Bell,
  Clock, CheckCircle, X, Loader2, Star, CalendarDays,
  AlertCircle, RotateCcw
} from "lucide-react";
import {
  collection, getDocs, query, where,
  orderBy, limit
} from "firebase/firestore";
import { db } from "../../../firebaseFunctions/config";

/* ─── AI draft helpers ───────────────────────────────── */
const generateAIDraft = async (description, eventName, organizer) => {
  try {
    const { getFunctions, httpsCallable } = await import("firebase/functions");
    const fn = httpsCallable(getFunctions(), "generateNotificationDraft");
    const res = await fn({ description, eventName, organizer });
    return res.data;
  } catch { return { success: false }; }
};

/* ─── Send helper ────────────────────────────────────── */
const doSend = async ({ eoUserId, recipients, subject, body, channels, eventName }) => {
  try {
    const { getFunctions, httpsCallable } = await import("firebase/functions");
    const fn = httpsCallable(getFunctions(), "sendVendorNotification");
    const res = await fn({ eoUserId, recipients, subject, body, channels, eventName });
    return res.data;
  } catch (e) { return { success: false, error: e.message }; }
};

/* ─── Component ──────────────────────────────────────── */
const NotificationHub = () => {
  const { user } = useAuth();
  const [vendors, setVendors]   = useState([]);
  const [events, setEvents]     = useState([]);
  const [history, setHistory]   = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [subject, setSubject]   = useState("");
  const [body, setBody]         = useState("");
  const [channels, setChannels] = useState({ email: true, sms: false, inApp: true });
  const [recipientMode, setRecipientMode] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [sending, setSending]   = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const aiRef = useRef(null);
  const { t } = useTranslation();

  const QUICK = [
    { label: t('notifications.weatherAlert'),    subject: "Weather Alert", value: "There is a weather advisory for the event day. Vendors need to secure their setups." },
    { label: t('notifications.setupTimeChange'), subject: "Setup Time Change", value: "The vendor setup time has changed. Please update your load-in plans." },
    { label: t('notifications.parkingUpdate'),   subject: "Parking Update", value: "Parking lot B is closed. All vendors must use lot C for load-in." },
    { label: t('notifications.generalReminder'), subject: "General Reminder", value: "Reminder of important event day details for all vendors." },
  ];

  /* ── Load ── */
  const loadData = useCallback(async () => {
    if (!user?.uid) return;
    setLoadingData(true);
    try {
      const [evSnap, vSnap, hSnap] = await Promise.all([
        getDocs(query(collection(db, "events"), where("userId", "==", user.uid), orderBy("startDate", "desc"), limit(20))),
        getDocs(collection(db, "vendorCRM", user.uid, "vendors")),
        getDocs(query(collection(db, "eoNotifications"), where("eoUserId", "==", user.uid), orderBy("sentAt", "desc"), limit(30)))
      ]);
      setEvents(evSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setVendors(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setHistory(hSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("NotificationHub:", e);
    } finally {
      setLoadingData(false);
    }
  }, [user?.uid]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Recipients ── */
  const recipients = (() => {
    if (recipientMode === "favorites") return vendors.filter(v => v.isFavorite);
    if (recipientMode === "event" && selectedEvent)
      return vendors.filter(v => (v.eventHistory || []).some(h => h.eventId === selectedEvent));
    return vendors;
  })();

  const canSend = subject.trim() && body.trim() && recipients.length > 0 && (channels.email || channels.sms || channels.inApp);

  /* ── AI draft ── */
  const handleAIDraft = async (prompt) => {
    const desc = (prompt ?? aiPrompt).trim();
    if (!desc) { aiRef.current?.focus(); return; }
    setAiLoading(true);
    setSendResult(null);
    const evName = events.find(e => e.id === selectedEvent)?.eventName || events[0]?.eventName || "";
    const res = await generateAIDraft(desc, evName, user?.displayName || "The Team");
    setAiLoading(false);
    if (res.success) { setSubject(res.subject); setBody(res.body); }
    else setSendResult({ success: false, message: t('notifications.aiFailed') });
  };

  /* ── Send ── */
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true); setSendResult(null);
    const res = await doSend({
      eoUserId: user.uid, recipients, subject, body, channels,
      eventName: events.find(e => e.id === selectedEvent)?.eventName || ""
    });
    setSending(false);
    if (res.success) {
      setSendResult({ success: true, message: res.sent !== 1 ? t('notifications.sentSuccessPlural', { count: res.sent }) : t('notifications.sentSuccess', { count: res.sent }) });
      setSubject(""); setBody(""); setAiPrompt("");
      loadData();
    } else {
      setSendResult({ success: false, message: res.error || t('notifications.sendFailed') });
    }
  };

  /* ── Render ── */
  if (loadingData) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
    </div>
  );

  const thisMonth = history.filter(h => {
    if (!h.sentAt?.toDate) return false;
    const d = h.sentAt.toDate();
    const n = new Date();
    return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  }).length;

  return (
    <div className="p-6 sm:p-8 max-w-5xl">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">{t('notifications.title')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{t('notifications.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { icon: Send,  color: "text-blue-500",    value: history.length,      label: t('notifications.totalSent') },
          { icon: Users, color: "text-emerald-500", value: vendors.length,      label: t('notifications.vendorContacts') },
          { icon: Clock, color: "text-orange-500",  value: thisMonth,           label: t('notifications.thisMonth') },
        ].map(({ icon: Icon, color, value, label }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <div>
              <p className="text-xl font-semibold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Compose card ── */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm mb-6">

        {/* AI bar */}
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-semibold text-gray-800">{t('notifications.aiAssist')}</span>
            <span className="text-xs text-gray-400 ml-1">{t('notifications.aiAssistDesc')}</span>
          </div>
          <div className="flex gap-2">
            <input
              ref={aiRef}
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleAIDraft()}
              placeholder={t('notifications.aiPlaceholder')}
              className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-300"
            />
            <button
              onClick={() => handleAIDraft()}
              disabled={aiLoading || !aiPrompt.trim()}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                aiLoading || !aiPrompt.trim()
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
            >
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {aiLoading ? t('notifications.drafting') : t('notifications.draftWithAI')}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {QUICK.map(q => (
              <button
                key={q.label}
                onClick={() => { setSubject(q.subject); setBody(q.value); }}
                disabled={aiLoading}
                className="px-2.5 py-1 text-xs bg-gray-50 border border-gray-200 rounded-full text-gray-600 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 transition disabled:opacity-50"
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fields */}
        <div className="p-5 space-y-4">

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('notifications.subject')}</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder={t('notifications.subjectPlaceholder')}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-400"
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-500">{t('notifications.message')}</label>
              {(body || subject) && (
                <button onClick={() => { setSubject(""); setBody(""); setAiPrompt(""); setSendResult(null); }}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
                  <RotateCcw className="w-3 h-3" /> {t('notifications.clear')}
                </button>
              )}
            </div>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder={t('notifications.messagePlaceholder')}
              rows={7}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-400 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1"><code className="bg-gray-100 px-1 rounded text-gray-500">{'{businessName}'}</code> {t('notifications.businessNameHint')}</p>
          </div>

          {/* Recipients + Channels + Send */}
          <div className="pt-2 border-t border-gray-100 space-y-3">

            {/* Row 1: Recipients */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">{t('notifications.sendTo')}</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { mode: "all",       icon: Users,        label: t('notifications.allVendors', { count: vendors.length }) },
                  { mode: "favorites", icon: Star,         label: t('notifications.favorites', { count: vendors.filter(v=>v.isFavorite).length }) },
                  { mode: "event",     icon: CalendarDays, label: t('notifications.byEvent') },
                ].map(({ mode, icon: Icon, label }) => (
                  <button
                    key={mode}
                    onClick={() => setRecipientMode(mode)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border font-medium transition ${
                      recipientMode === mode
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    <Icon className="w-3 h-3" /> {label}
                  </button>
                ))}
              </div>
              {recipientMode === "event" && (
                <select
                  value={selectedEvent}
                  onChange={e => setSelectedEvent(e.target.value)}
                  className="mt-2 text-sm border border-gray-200 rounded-lg px-3 py-1.5 max-w-xs focus:outline-none focus:ring-1 focus:ring-gray-300"
                >
                  <option value="">{t('notifications.selectEvent')}</option>
                  {events.map(ev => <option key={ev.id} value={ev.id}>{ev.eventName}</option>)}
                </select>
              )}
            </div>

            {/* Row 2: Channels + Send */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">{t('notifications.sendVia')}</p>
                <div className="flex gap-1.5">
                  {[
                    { key: "email",  icon: Mail,          label: t('notifications.email') },
                    { key: "sms",    icon: MessageSquare, label: t('notifications.sms') },
                    { key: "inApp",  icon: Bell,          label: t('notifications.inApp') },
                  ].map(({ key, icon: Icon, label }) => (
                    <button
                      key={key}
                      onClick={() => setChannels(prev => ({ ...prev, [key]: !prev[key] }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border font-medium transition ${
                        channels[key]
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <Icon className="w-3 h-3" /> {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSend}
                disabled={!canSend || sending}
                className={`sm:ml-auto flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition shadow-sm ${
                  canSend && !sending
                    ? "bg-gray-900 text-white hover:bg-gray-800 hover:shadow"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                }`}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? t('notifications.sending') : (recipients.length !== 1 ? t('notifications.sendToVendorsPlural', { count: recipients.length }) : t('notifications.sendToVendors', { count: recipients.length }))}
              </button>
            </div>

            {/* Result */}
            {sendResult && (
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm ${
                sendResult.success
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {sendResult.success
                  ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                <span>{sendResult.message}</span>
                <button onClick={() => setSendResult(null)} className="ml-auto opacity-60 hover:opacity-100">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Sent history ── */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" /> {t('notifications.sentHistory')}
        </h2>
        {history.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">{t('notifications.noNotificationsSent')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map(h => {
              const date = h.sentAt?.toDate?.();
              const dateStr = date
                ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                : t('notifications.justNow');
              return (
                <div key={h.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium text-gray-900 truncate">{h.subject || t('notifications.noSubject')}</p>
                      <div className="flex items-center gap-1 text-gray-400 flex-shrink-0">
                        {h.channels?.email && <Mail className="w-3 h-3" />}
                        {h.channels?.sms   && <MessageSquare className="w-3 h-3" />}
                        {h.channels?.inApp && <Bell className="w-3 h-3" />}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-1">{h.body?.slice(0, 120)}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-400">
                        {h.recipientCount ?? "?"} {t('notifications.recipients')}
                        {h.eventName ? ` · ${h.eventName}` : ""}
                      </span>
                      <span className="text-xs text-gray-400">{dateStr}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationHub;
